// src/utils/asignacion-utilidad.js
import axios from "axios";
import {
  API_URL_instructores,
  API_URL_proyecto_summary,
  API_URL_utilidad,
} from "../constants";

/**
 * Partners excluded from instructor assignment commission.
 * Their portion stays with LSSI.
 */
const EXCLUDED_PARTNERS = ["LSSI Europa"];

/**
 * Assignment pool percentage of total utilidad
 */
const ASSIGNMENT_POOL_PERCENT = 12.5;

/**
 * Stage percentages (37.5% total when there are instructors).
 * When the project has no instructors, the 12.5% instructor-assignment pool
 * collapses into Coaching, so coaching becomes 25% (stages total 50%).
 */
const STAGE_PERCENTAGES = {
  contacto: 3.75,
  cita: 3.75,
  diagnostico: 2.5,
  propuesta: 5,
  cierre: 2.5,
  kickoff: 2.5,
  seguimiento: 2.5,
  reunioncierre: 2.5,
  coaching: 12.5,
};

const COACHING_PERCENT_NO_INSTRUCTORS = 25;

function buildStagePercentages(hasInstructors) {
  return {
    ...STAGE_PERCENTAGES,
    coaching: hasInstructors ? STAGE_PERCENTAGES.coaching : COACHING_PERCENT_NO_INSTRUCTORS,
  };
}

/**
 * Recompute and persist ALL utilidades for a proyecto:
 * 1. Stage utilidades (37.5%) - recalculate amounts based on current utilidad
 * 2. Assignment utilidades (12.5%) - recalculate based on instructor income proportions
 * 
 * @param {string} proyecto_code - Project code
 * @param {Object} headers - Auth headers
 * @param {Object} options - Optional settings
 * @param {boolean} options.recalculateStages - Whether to recalculate stage amounts (default: true)
 * @param {boolean} options.recalculateAssignments - Whether to recalculate assignments (default: true)
 */
export async function recomputeAsignacionUtilidades(proyecto_code, headers, options = {}) {
  const {
    recalculateStages = true,
    recalculateAssignments = true,
    hasInstructors = true,
  } = options;
  const stagePercentages = buildStagePercentages(hasInstructors);

  if (!proyecto_code) {
    console.warn("[Utilidades] Skipped: no proyecto_code provided");
    return { success: false, reason: "no proyecto_code" };
  }

  try {
    // 1) Fetch current utilidad for the proyecto
    const { data: summary } = await axios.get(API_URL_proyecto_summary, {
      headers,
      params: { proyecto_code },
    });
    const utilidad = Number(summary?.utilidad || 0);

    console.log("[Utilidades] Utilidad:", utilidad);

    if (utilidad <= 0) {
      console.warn("[Utilidades] Utilidad is 0, skipping");
      return { success: true, reason: "no utilidad", saved: 0 };
    }

    // 2) Fetch existing utilidades to get current partner assignments
    const { data: existingUtilidades = [] } = await axios.get(API_URL_utilidad, {
      headers,
      params: { proyecto_code },
    });

    const existingByType = {};
    existingUtilidades
      .filter((u) => u.proyecto_code === proyecto_code)
      .forEach((u) => {
        existingByType[u.tipo_utilidad] = u;
      });

    console.log("[Utilidades] Existing utilidades:", Object.keys(existingByType));

    const payloads = [];

    // 3) Recalculate STAGE amounts (37.5%)
    if (recalculateStages) {
      Object.entries(stagePercentages).forEach(([stage, percent]) => {
        const existing = existingByType[stage];
        
        // Only update if a partner is already assigned
        if (existing && existing.partner) {
          const cantidad = utilidad * (percent / 100);
          
          payloads.push({
            proyecto_code,
            tipo_utilidad: stage,
            porcentaje: percent,
            cantidad,
            partner: existing.partner,
          });
        }
      });
    }

    // 4) Recalculate ASSIGNMENT amounts (12.5%)
    if (recalculateAssignments) {
      // Fetch instructors
      const { data: instructors = [] } = await axios.get(API_URL_instructores, {
        headers,
        params: { proyecto_code },
      });

      console.log("[Utilidades] Instructors fetched:", instructors.length);

      if (Array.isArray(instructors) && instructors.length > 0) {
        // Calculate income by partner
        const incomeByPartner = {};
        let totalEligibleIncome = 0;

        instructors.forEach((inst) => {
          const partner = (inst.miembro_que_asigna || "").trim();
          if (!partner) return;

          const horas = Number(inst.horas || 0);
          const semanas = Number(inst.semanas || 0);
          const tarifa = Number(inst.tarifa_hora || 0);
          const costo = Number(inst.costo_hora || 0);
          // Use tarifa_hora for proportion; fallback to costo_hora if tarifa is 0
          const income = horas * semanas * (tarifa > 0 ? tarifa : costo);

          // Skip excluded partners
          if (EXCLUDED_PARTNERS.includes(partner)) {
            console.log(`[Utilidades] Excluding ${partner} from assignment commission`);
            return;
          }

          incomeByPartner[partner] = (incomeByPartner[partner] || 0) + income;
          totalEligibleIncome += income;
        });

        console.log("[Utilidades] Income by partner:", incomeByPartner);
        console.log("[Utilidades] Total eligible income:", totalEligibleIncome);

        // Build assignment payloads
        if (totalEligibleIncome > 0) {
          Object.entries(incomeByPartner).forEach(([partner, partnerIncome]) => {
            const proportion = partnerIncome / totalEligibleIncome;
            const porcentaje = proportion * ASSIGNMENT_POOL_PERCENT;
            const cantidad = utilidad * (porcentaje / 100);

            const tipo_utilidad = `Asignacion - ${partner}`;

            payloads.push({
              proyecto_code,
              tipo_utilidad,
              porcentaje,
              cantidad,
              partner,
            });
          });
        }
      }
    }

    console.log("[Utilidades] Payloads to save:", payloads.length);

    // 5) Execute all POSTs
    const posts = payloads.map((payload) =>
      axios.post(API_URL_utilidad, payload, { headers })
    );

    const results = await Promise.allSettled(posts);

    // Log and count results
    let savedCount = 0;
    let failedCount = 0;

    results.forEach((result, idx) => {
      if (result.status === "fulfilled") {
        console.log(
          `[Utilidades] ✅ Saved: ${payloads[idx].tipo_utilidad} = ${payloads[idx].cantidad.toFixed(2)}`
        );
        savedCount++;
      } else {
        console.error(
          `[Utilidades] ❌ Failed: ${payloads[idx].tipo_utilidad}`,
          result.reason?.response?.data || result.reason
        );
        failedCount++;
      }
    });

    return {
      success: failedCount === 0,
      saved: savedCount,
      failed: failedCount,
      payloads,
      results,
    };
  } catch (error) {
    console.error("[Utilidades] Error:", error);
    return { success: false, reason: "error", error };
  }
}

/**
 * Check if a partner is excluded from assignment commission
 */
export function isExcludedFromAssignment(partner) {
  return EXCLUDED_PARTNERS.includes((partner || "").trim());
}

/**
 * Get the assignment pool percentage
 */
export function getAssignmentPoolPercent() {
  return ASSIGNMENT_POOL_PERCENT;
}

/**
 * Get the list of excluded partners
 */
export function getExcludedPartners() {
  return [...EXCLUDED_PARTNERS];
}

/**
 * Get stage percentages. Pass hasInstructors=false to get the collapsed map
 * where coaching absorbs the 12.5% that would normally go to instructor assignment.
 */
export function getStagePercentages(hasInstructors = true) {
  return buildStagePercentages(hasInstructors);
}

/**
 * Get the coaching percentage for the current instructor state.
 */
export function getCoachingPercent(hasInstructors = true) {
  return hasInstructors
    ? STAGE_PERCENTAGES.coaching
    : COACHING_PERCENT_NO_INSTRUCTORS;
}